import React from 'react';
import { MediaLibraryItem } from '../../../api/media';

export type SelectionMode = 'single' | 'multi';

const mediaKey = (item: { media_id?: string; id?: string }): string =>
  item.media_id || item.id || '';

/**
 * Page-independent media selection model — the single source of truth for which
 * items are picked, shared by every library surface (the attachment modal's
 * `select` mode and any multi-select picker).
 *
 * Selection stores the **full item snapshot** keyed by media id (`byId`), not
 * just the id. This is what makes it survive pagination/filter/search: an entry
 * is only ever added or removed by an explicit user action (toggle/remove/clear)
 * or a deliberate seed — never by a page swap. `ids` is kept in lock-step as the
 * ordered key list so consumers that render a "selected" checkmark can read it
 * cheaply, and the derived `selectedItems` follows click/seed order.
 *
 * Without this, resolving picks against the currently-loaded page silently drops
 * selections made on other pages (the cross-page bug). With it, confirm just
 * returns `selectedItems` — no re-resolution against the visible page.
 *
 * `mode`:
 *   - `'multi'`: unbounded selection.
 *   - `'single'`: at most one entry (toggling a new item replaces the old) —
 *     lets a single-pick picker reuse the exact same model.
 */
export interface SelectionApi {
  /** Item snapshots keyed by media id. */
  byId: Record<string, MediaLibraryItem>;
  /** Ordered selected media ids (click/seed order). */
  ids: string[];
  /** Selected items, in order, with unresolved ids filtered out. */
  selectedItems: MediaLibraryItem[];
  /** Whether a media id is currently selected. */
  has: (mediaId: string) => boolean;
  /** Number of selected items. */
  count: number;
  /**
   * Toggle an item in/out of the selection. In `single` mode, selecting a new
   * item replaces any existing pick; re-toggling the current pick clears it.
   */
  toggle: (item: MediaLibraryItem) => void;
  /** Add many item snapshots idempotently (used by cross-page select-all). */
  addMany: (items: MediaLibraryItem[]) => void;
  /** Remove a media id from the selection (e.g. after deleting the item). */
  remove: (mediaId: string) => void;
  /** Clear the entire selection. */
  clear: () => void;
  /**
   * Seed the selection. Prefers the full `items` (they survive even if their
   * page never loads); falls back to `fallbackIds` for ids-only callers, whose
   * objects resolve later via {@link mergeLoadedItems} when their page appears.
   */
  seed: (items: MediaLibraryItem[], fallbackIds: string[]) => void;
  /**
   * Merge freshly-loaded page items into the stored snapshots for any id already
   * in the selection. Resolves ids seeded without an object and refreshes a
   * stored object (e.g. a favourite toggle) on revisited pages — without ever
   * dropping picks from pages that aren't currently loaded.
   */
  mergeLoadedItems: (items: MediaLibraryItem[]) => void;
}

export const useSelection = (mode: SelectionMode): SelectionApi => {
  const [byId, setById] = React.useState<Record<string, MediaLibraryItem>>({});
  const [ids, setIds] = React.useState<string[]>([]);

  // Read the latest ids inside callbacks/effects without making them depend on
  // `ids` (keeps consumer callbacks stable so memoized rows don't re-render on
  // every toggle).
  const idsRef = React.useRef(ids);
  idsRef.current = ids;

  const selectedItems = React.useMemo(
    () =>
      ids
        .map((id) => byId[id])
        .filter((item): item is MediaLibraryItem => Boolean(item)),
    [ids, byId]
  );

  const has = React.useCallback((mediaId: string) => idsRef.current.includes(mediaId), []);

  const toggle = React.useCallback(
    (item: MediaLibraryItem) => {
      const mediaId = mediaKey(item);
      if (!mediaId) return;
      const alreadySelected = idsRef.current.includes(mediaId);
      if (alreadySelected) {
        setIds((previous) => previous.filter((id) => id !== mediaId));
        setById((previous) => {
          if (!(mediaId in previous)) return previous;
          const next = { ...previous };
          delete next[mediaId];
          return next;
        });
        return;
      }
      if (mode === 'single') {
        // At most one pick: a new selection replaces any existing one.
        setIds([mediaId]);
        setById({ [mediaId]: item });
        return;
      }
      setIds((previous) => (previous.includes(mediaId) ? previous : [...previous, mediaId]));
      setById((previous) => ({ ...previous, [mediaId]: item }));
    },
    [mode]
  );

  const remove = React.useCallback((mediaId: string) => {
    setIds((previous) => previous.filter((id) => id !== mediaId));
    setById((previous) => {
      if (!(mediaId in previous)) return previous;
      const next = { ...previous };
      delete next[mediaId];
      return next;
    });
  }, []);

  const addMany = React.useCallback(
    (items: MediaLibraryItem[]) => {
      const valid = items
        .map((item) => ({ id: mediaKey(item), item }))
        .filter((entry) => Boolean(entry.id));
      if (valid.length === 0) return;
      if (mode === 'single') {
        const first = valid[0];
        setIds([first.id]);
        setById({ [first.id]: first.item });
        return;
      }
      setIds((previous) => {
        const seen = new Set(previous);
        const additions: string[] = [];
        valid.forEach(({ id }) => {
          if (seen.has(id)) return;
          seen.add(id);
          additions.push(id);
        });
        return additions.length > 0 ? [...previous, ...additions] : previous;
      });
      setById((previous) => {
        let changed = false;
        const next = { ...previous };
        valid.forEach(({ id, item }) => {
          if (previous[id] !== item) {
            next[id] = item;
            changed = true;
          }
        });
        return changed ? next : previous;
      });
    },
    [mode]
  );

  const clear = React.useCallback(() => {
    setIds([]);
    setById({});
  }, []);

  const seed = React.useCallback(
    (items: MediaLibraryItem[], fallbackIds: string[]) => {
      const seedMap: Record<string, MediaLibraryItem> = {};
      items.forEach((item) => {
        const id = mediaKey(item);
        if (id) seedMap[id] = item;
      });
      const seedIds = items.length > 0 ? items.map(mediaKey).filter(Boolean) : fallbackIds;
      // In single mode, a seed of multiple items keeps only the first to honour
      // the ≤1 invariant (callers rarely seed multi into a single picker, but
      // the model stays consistent if they do).
      if (mode === 'single' && seedIds.length > 1) {
        const first = seedIds[0];
        setById(first in seedMap ? { [first]: seedMap[first] } : {});
        setIds(first ? [first] : []);
        return;
      }
      setById(seedMap);
      setIds(seedIds);
    },
    [mode]
  );

  const mergeLoadedItems = React.useCallback((items: MediaLibraryItem[]) => {
    if (items.length === 0) return;
    const selectedSet = new Set(idsRef.current);
    if (selectedSet.size === 0) return;
    setById((previous) => {
      let changed = false;
      const next = { ...previous };
      items.forEach((item) => {
        const id = mediaKey(item);
        // Only write when the stored snapshot is genuinely a different object —
        // a referential equality guard. Callers may pass a freshly-rebuilt list
        // (new array identity) on every render; without this guard re-storing an
        // already-current item would yield a new `byId` each time and, paired
        // with an effect keyed on that list, spin an infinite render loop.
        if (id && selectedSet.has(id) && previous[id] !== item) {
          next[id] = item;
          changed = true;
        }
      });
      return changed ? next : previous;
    });
  }, []);

  return {
    byId,
    ids,
    selectedItems,
    has,
    count: ids.length,
    toggle,
    addMany,
    remove,
    clear,
    seed,
    mergeLoadedItems,
  };
};
